/**
 * TK-46 §1 (lee 25/10: "ẩn / hiện theo ngữ cảnh") — luật THUẦN của khối "Điều khiển độ sáng" ở Vận hành. Không React, không mạng.
 *   · `phamViHien`: phạm vi nào hiện, mặc định là gì — theo đối tượng đang chọn (bảng mục 1 của phiếu).
 *   · `dichNhom`: đích của lệnh NHÓM = đèn có `nhomTb` = N VÀ nhận lệnh (`nhanLenh`, luật TK-44 §2); đèn chưa rõ nhóm (null) không tính.
 *   · `mucChung`: mức chung của các đèn nhận lệnh — khác nhau / chưa biết ⇒ null (thanh "— %", Gửi chờ kéo).
 */
import type { LyDoKhongNhan } from './nemaNhan';

export type PhamVi = 'tuyen' | 'tu' | 'nhom' | 'den';
export const NHAN_PHAM_VI: Record<PhamVi, string> = { tuyen: 'Tuyến', tu: 'Tủ', nhom: 'Nhóm', den: 'Đèn' };

/** Đang chọn gì (id hoặc null). Đèn đang chọn ⇒ tủ của đèn. */
export interface ChonVanHanh { tuyen: string | null; tu: string | null; den: string | null }

/** Bảng mục 1: chưa chọn tuyến ⇒ không khối · tuyến ⇒ Tuyến · Nhóm · tủ ⇒ Tủ · Nhóm · Tuyến · đèn ⇒ Đèn · Tủ · Nhóm. */
export function phamViHien(c: ChonVanHanh): { ds: PhamVi[]; macDinh: PhamVi | null } {
  if (c.den) return { ds: ['den', 'tu', 'nhom'], macDinh: 'den' };
  if (c.tu) return { ds: ['tu', 'nhom', 'tuyen'], macDinh: 'tu' };
  if (c.tuyen) return { ds: ['tuyen', 'nhom'], macDinh: 'tuyen' };
  return { ds: [], macDinh: null };
}

export interface DenDk { cabinetId: string; addr: number; nhomTb?: number | null; nhanLenh?: boolean; lyDo?: LyDoKhongNhan | null; dim?: number | null }

/** Đích nhóm N trong một tập đèn (đã giới hạn theo phạm vi tủ / tuyến). */
export function dichNhom<T extends DenDk>(den: readonly T[], n: number): { trongNhom: T[]; nhan: T[]; chuaRo: number } {
  const trongNhom = den.filter((d) => d.nhomTb === n);
  return { trongNhom, nhan: trongNhom.filter((d) => d.nhanLenh === true), chuaRo: den.filter((d) => d.nhomTb == null).length };
}

/** Mức chung của các đèn NHẬN lệnh: mọi đèn cùng một mức đã biết ⇒ mức ấy; khác nhau / có đèn chưa biết / rỗng ⇒ null. */
export function mucChung(den: readonly DenDk[]): number | null {
  const ds = den.filter((d) => d.nhanLenh === true);
  if (ds.length === 0) return null;
  const m = ds[0].dim;
  return m != null && ds.every((d) => d.dim === m) ? m : null;
}

/** Lý do đầu tiên trong các đèn KHÔNG nhận lệnh (để thu khối còn một dòng). */
export const lyDoDau = (den: readonly DenDk[]): LyDoKhongNhan | null => den.find((d) => d.nhanLenh !== true && d.lyDo)?.lyDo ?? null;

/**
 * TK-47 §1 — "ĐANG BÁO": mức thiết bị báo về cho phạm vi đang chọn.
 *   · Đèn: mức đèn báo; số đo không hiện hành (`soDoHienHanh = false`, TK-44) ⇒ null ("—").
 *   · Tủ / tuyến / nhóm: mức CHUNG của các đèn nhận lệnh (`mucChung`); khác mức / chưa biết ⇒ null (TK-46). KHÔNG mặc định 100.
 */
export function dangBao(den: (DenDk & { soDoHienHanh?: boolean }) | null, ds: readonly DenDk[]): number | null {
  if (den) return den.soDoHienHanh === false ? null : den.dim ?? null;
  return mucChung(ds);
}

/** TK-47 §1: nút đặt nhanh — CHỈ đổi "Muốn đặt", không gửi lệnh. 0 = "Tắt". */
export const DAT_NHANH = [0, 25, 50, 75, 100] as const;

/**
 * TK-48 §2 (lee chọn theo hẳn bản ChatGPT v06) — BỐ CỤC trang Vận hành theo bề rộng cửa sổ.
 *   · < 900 px: `xep-doc` — bản đồ rộng hết; khung điều khiển là TẤM TRƯỢT nổi ở nửa dưới màn; cây ở ngăn kéo.
 *   · 900 … 1199: `cay-gap` — cây gập thành nút "Đổi đối tượng"; khung điều khiển NỔI trên bản đồ (góc phải).
 *   · ≥ 1200: `ba-cot` — cột cây "Tuyến & thiết bị" · bản đồ có khung điều khiển nổi.
 * TK-47 đặt ngưỡng cây 1680 vì khối điều khiển là một CỘT riêng ăn bề rộng bản đồ; nay khung nổi trên bản đồ ⇒ về 1200 như phiếu.
 * Luật thay "không khối nào đè bản đồ": vật đang chọn KHÔNG BAO GIỜ nằm dưới khung (`vungTuDo` + `panInside`).
 */
export type BoCuc = 'ba-cot' | 'cay-gap' | 'xep-doc';
export const NGUONG_XEP_DOC = 900;
export const NGUONG_BA_COT = 1200;
export function boCucVanHanh(rong: number): BoCuc {
  if (rong < NGUONG_XEP_DOC) return 'xep-doc';
  return rong >= NGUONG_BA_COT ? 'ba-cot' : 'cay-gap';
}

/** Hộp chữ nhật theo toạ độ màn hình (như `getBoundingClientRect`). */
export interface Hop { x: number; y: number; w: number; h: number }
/** Lề an toàn quanh vùng tự do (px) và dải trên cùng dành cho thanh công cụ bản đồ. */
export const LE_VUNG = 16;
export const DAI_CONG_CU = 60;

/**
 * TK-48 §2 — VÙNG TỰ DO trên bản đồ: nơi vật đang chọn được phép nằm (đo bbox khung THẬT, không số cứng).
 *   · `noi` (khung nổi góc phải): phần bản đồ BÊN TRÁI khung, chừa dải công cụ trên cùng.
 *   · `truot` (tấm trượt dưới): phần bản đồ còn nhìn thấy TRÊN tấm, lấy NỬA TRÊN (phiếu: đèn ở nửa trên phần còn thấy).
 *   · khung null / không chạm bản đồ ⇒ cả bản đồ trừ lề.
 * Bất biến (kiem-tk48): vùng nằm TRONG bản đồ và KHÔNG giao bbox khung; vùng quá hẹp vẫn ≥ 1 px để panInside có đích.
 */
export function vungTuDo(ban: Hop, khung: Hop | null, kieu: 'noi' | 'truot', vh = Infinity): Hop {
  const trai = ban.x + LE_VUNG, tren = ban.y + Math.min(DAI_CONG_CU, ban.h / 4);
  let phai = ban.x + ban.w - LE_VUNG, duoi = Math.min(ban.y + ban.h, vh) - LE_VUNG;
  const cham = khung && khung.x < ban.x + ban.w && khung.x + khung.w > ban.x && khung.y < ban.y + ban.h && khung.y + khung.h > ban.y;
  if (khung && cham) {
    if (kieu === 'noi') phai = Math.min(phai, khung.x - LE_VUNG);
    else {
      const dayThay = Math.min(duoi, khung.y - LE_VUNG);
      duoi = tren + (dayThay - tren) / 2;
    }
  }
  const w = Math.max(1, phai - trai), h = Math.max(1, duoi - tren);
  return { x: trai, y: tren, w, h };
}

/** Đổi vùng tự do thành lề cho `map.panInside` (toạ độ trong khung bản đồ). */
export function lePanInside(ban: Hop, vung: Hop): { paddingTopLeft: [number, number]; paddingBottomRight: [number, number] } {
  return {
    paddingTopLeft: [Math.max(0, vung.x - ban.x), Math.max(0, vung.y - ban.y)],
    paddingBottomRight: [Math.max(0, ban.x + ban.w - (vung.x + vung.w)), Math.max(0, ban.y + ban.h - (vung.y + vung.h))],
  };
}

/** TK-48 §2 — chiều cao tấm trượt: thấp = một hàng (đầu khung), cao tối đa 70 % màn; kéo thả về mốc gần nhất dưới 120 px ⇒ thấp. */
export const CAO_THAP = 64;
export function caoTamTruot(keo: number, vh: number): number {
  const tran = Math.round(vh * 0.7);
  if (!Number.isFinite(keo)) return tran;
  if (keo < 120) return CAO_THAP;
  return Math.min(tran, Math.max(CAO_THAP, Math.round(keo)));
}

/**
 * TK-48 §2 — số mức trong khung: "Mức sáng đang ghi nhận" → "Muốn đặt". Hai mức trùng nhau (hoặc chưa chọn mức muốn đặt) ⇒ MỘT số
 *   lớn như ảnh v06. Chưa chọn ⇒ Gửi khoá (TK-47 giữ).
 */
export function cachHienMuc(dangBaoMuc: number | null, muonDat: number | null): 'mot' | 'hai' {
  return muonDat === null || muonDat === dangBaoMuc ? 'mot' : 'hai';
}
